import { Async } from '@client/new/helpers/types';
import { useEffect, useState } from 'react';
import { HomeModel } from '../home.model';
import { Influencer } from '@playbook-technologies/common-enums';

export type ModalType =
  | 'None'
  | 'QrCode'
  | 'GoLive'
  | 'LiveSuccess'
  | 'Webinar'
  | 'Links'
  | 'SuperTooltip'
  | 'BlackFridayChallenge'
  | 'Links';

type UseHomeModalsParams = {
  model: Async<HomeModel>;
};

type UseHomeModalsReturn = {
  modalType: ModalType;
  openModal: (type: ModalType) => void;
  closeModal: () => void;
  isModalOpen: (type: ModalType) => boolean;
};

export const useHomeModals = (
  p: UseHomeModalsParams,
): Async<UseHomeModalsReturn> => {
  const [modalType, setModalType] = useState<ModalType>('None');

  // Effect for LiveSuccess modal - shows when creator goes live
  useEffect(() => {
    if (p.model.type !== 'fetched') return;
    if (p.model.value.creatorWentLive) {
      setModalType('LiveSuccess');
    }
  }, [p.model.type]);

  // Effect for conditional modals based on onboarding state
  useEffect(() => {
    if (p.model.type !== 'fetched') return;

    const mode = p.model.value.data?.creator?.mode;
    const isOnboarding = mode === Influencer.Mode.Onboarding.value;

    const modalSeen = localStorage.getItem('webinarModalSeen');

    if (isOnboarding && !modalSeen) {
      setModalType('Webinar');
      localStorage.setItem('webinarModalSeen', 'true');
    }
  }, [p.model.type]);

  if (p.model.type !== 'fetched') return p.model;

  const openModal = (type: ModalType) => {
    setModalType(type);
  };

  const closeModal = () => {
    setModalType('None');
  };

  const isModalOpen = (type: ModalType) => {
    return modalType === type;
  };

  return {
    type: 'fetched',
    value: {
      modalType,
      openModal,
      closeModal,
      isModalOpen,
    },
  };
};
